/*
  <quantity-input> and <product-form>, the add to cart elements.

  Loaded from layout/theme.liquid on every page rather than bundled with the
  _buy-buttons block: product cards and pairing cards wrap their forms in
  <product-form> too, on pages where that block is never rendered.
*/
if (!customElements.get('quantity-input')) {
  customElements.define(
    'quantity-input',
    class QuantityInput extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.decrease = this.querySelector('[data-quantity-step="-1"]');
        if (!this.input) return;

        this.querySelectorAll('[data-quantity-step]').forEach((button) => {
          button.addEventListener('click', () => {
            const step = Number(button.dataset.quantityStep);
            step > 0 ? this.input.stepUp() : this.input.stepDown();
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });

        this.input.addEventListener('change', () => this.sync());
        this.sync();
      }

      sync() {
        const min = Number(this.input.min) || 1;
        if (!(Number(this.input.value) >= min)) this.input.value = min;
        if (this.decrease) this.decrease.disabled = Number(this.input.value) <= min;
      }
    }
  );
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;

        this.variantInput = this.form.querySelector('[data-variant-input]');
        this.submitButton = this.form.querySelector('[data-submit]');
        this.planRadio = this.form.querySelector('[data-plan-radio]');
        this.planSelect = this.form.querySelector('[data-plan-select]');
        this.gift = this.form.querySelector('[data-gift-variant-id]');
        this.error = this.form.querySelector('[data-error]');

        try {
          this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
        } catch {
          this.variants = [];
        }

        this.variant = this.variants.find((variant) => String(variant.id) === this.variantInput?.value);

        // A gift add redirects to the cart while still busy; coming back to
        // this page from the back/forward cache must not restore a spinner.
        window.addEventListener('pageshow', () => this.setBusy(false));

        this.addEventListener('change', (event) => this.onChange(event));
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
      }

      onChange(event) {
        const target = event.target;

        if (target.matches('[data-option]')) {
          const selected = Array.from(this.querySelectorAll('[data-option]:checked'), (input) => input.value);
          const variant = this.variants.find((candidate) =>
            candidate.options.every((value, index) => value === selected[index])
          );
          this.setVariant(variant);
        }

        // Choosing a frequency is choosing to subscribe.
        if (target.matches('[data-plan-select]') && this.planRadio) {
          this.planRadio.value = target.value;
          this.planRadio.checked = true;
          this.renderPlan();
        }
      }

      setVariant(variant) {
        this.variant = variant;
        this.hideError();

        if (!variant) {
          this.setSubmit(false, this.dataset.labelUnavailable);
          return;
        }

        this.variantInput.value = variant.id;
        this.setSubmit(variant.available, variant.available ? this.dataset.labelAdd : this.dataset.labelSoldOut);

        const price = this.form.querySelector('[data-price]');
        if (price) price.textContent = variant.price;
        this.renderPlan();

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        this.dispatchEvent(
          new CustomEvent('product:variant-change', {
            bubbles: true,
            detail: { variant, sectionId: this.dataset.sectionId },
          })
        );
      }

      renderPlan() {
        if (!this.planRadio || !this.variant) return;

        const plan = this.variant.plans?.[this.planRadio.value];
        const price = this.form.querySelector('[data-plan-price]');
        const savings = this.form.querySelector('[data-plan-savings]');

        if (price) price.textContent = plan ? plan.price : this.variant.price;
        if (savings) {
          savings.hidden = !plan || plan.savings <= 0;
          if (plan) savings.textContent = plan.savings_label;
        }
      }

      setSubmit(enabled, label) {
        if (!this.submitButton) return;
        this.submitButton.disabled = !enabled;
        this.submitButton.textContent = label;
      }

      // A cart drawer or notification on the page ([data-cart-surface], only
      // rendered when the Cart type setting asks for one) means the add goes
      // through the Ajax API and asks for that section in the same request.
      // Without one, the native post already lands on the cart page — only a
      // ticked gift, which needs a second line, still has to go through Ajax.
      async onSubmit(event) {
        const surface = document.querySelector('[data-cart-surface]');
        if (!surface && !this.gift?.checked) return;
        event.preventDefault();

        const data = new FormData(this.form);
        const item = {
          id: Number(data.get('id')),
          quantity: Number(data.get('quantity')) || 1,
        };
        const sellingPlan = data.get('selling_plan');
        if (sellingPlan) item.selling_plan = Number(sellingPlan);

        const items = [item];
        if (this.gift?.checked) items.push({ id: Number(this.gift.dataset.giftVariantId), quantity: 1 });

        const body = { items };
        if (surface) {
          body.sections = surface.dataset.sectionId;
          body.sections_url = window.location.pathname;
        }

        this.setBusy(true, event.submitter);
        this.hideError();

        try {
          const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const json = await response.json().catch(() => ({}));

          if (!response.ok) {
            throw new Error(json.description || json.message || this.dataset.labelError);
          }

          if (!surface) {
            window.location.assign(this.dataset.cartUrl);
            return;
          }

          this.setBusy(false);
          document.dispatchEvent(
            new CustomEvent('cart:add', { detail: { items: json.items || [], sections: json.sections } })
          );
        } catch (error) {
          this.setBusy(false);
          this.showError(error.message || this.dataset.labelError);
        }
      }

      // The button that was pressed spins (critical.css draws the spinner for
      // any busy button); the element itself is marked too, for the rest of
      // the form to read.
      setBusy(busy, button) {
        if (busy) {
          this.busyButton = button || this.form.querySelector('[type="submit"]');
          this.setAttribute('aria-busy', 'true');
          this.busyButton?.setAttribute('aria-busy', 'true');
          return;
        }

        this.removeAttribute('aria-busy');
        this.busyButton?.removeAttribute('aria-busy');
        this.busyButton = null;
      }

      showError(message) {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = false;
      }

      hideError() {
        if (this.error) this.error.hidden = true;
      }
    }
  );
}
